import React from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import {
  border, textPrimary, textSecondary, accent, font,
  success, warning, error as errorColor, infoBlue, easing, avatarColorFor,
} from "../../layouts/schoolPortal/schoolPortalTheme";

/**
 * The ranked list of records to open today.
 *
 * Apollo's home is not charts, it is "these 14 accounts need you". Every row
 * here names a person, says why in words, and opens their record. A row that
 * cannot say why it is there does not belong on this page.
 *
 * URGENCY IS TIME PRESSURE, NOT SEVERITY
 * A booking request expiring tonight outranks a student who has been quiet for
 * three weeks, because one of them stops being actionable at midnight.
 */

const REASON_STYLE = {
  expiring_request:  { color: errorColor, label: "Expiring" },
  awaiting_approval: { color: warning,    label: "Approve" },
  failed_payment:    { color: errorColor, label: "Payment" },
  session_today:     { color: infoBlue,   label: "Today" },
  unfilled_group:    { color: infoBlue,   label: "Group" },
  overdue_task:      { color: warning,    label: "Overdue" },
  at_risk:           { color: textSecondary, label: "At risk" },
};

const style = (r) => REASON_STYLE[r] || { color: textSecondary, label: r };

export default function WorkQueue({ data, navigate }) {
  const queue = data?.queue || [];
  const groups = data?.groupSessions || [];
  const scope = data?.scope;

  return (
    <Box sx={{ backgroundColor: "#fff", border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden" }}>
      <Box sx={{ px: 1.75, py: 1.25, borderBottom: `1px solid ${border}`, display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography sx={{ fontFamily: font, fontSize: "14px", fontWeight: 800, color: textPrimary }}>
          Needs you {queue.length ? `(${queue.length})` : ""}
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary }}>
          {scope === "instructor" ? "your students" : "across the school"} · most time-critical first
        </Typography>
      </Box>

      {queue.map((row) => (
        <Box
          key={row.studentId}
          onClick={() => navigate(`/school/students/${row.studentId}`)}
          sx={{
            display: "flex", gap: 1.25, px: 1.75, py: 1.1, cursor: "pointer",
            borderBottom: `1px solid ${border}`, transition: `background 0.12s ${easing}`,
            "&:last-of-type": { borderBottom: "none" },
            "&:hover": { backgroundColor: "#FAF8F5" },
          }}
        >
          <Box sx={{
            width: 28, height: 28, borderRadius: "8px", flexShrink: 0, mt: 0.2,
            backgroundColor: avatarColorFor(row.name), color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: font, fontWeight: 700, fontSize: "11px",
          }}>
            {row.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
              {row.name}
            </Typography>
            {row.reasons.map((r, i) => {
              const st = style(r.reason);
              return (
                <Box key={i} sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 0.15 }}>
                  <Typography sx={{
                    fontFamily: font, fontSize: "10px", fontWeight: 700, letterSpacing: "0.05em",
                    textTransform: "uppercase", color: st.color, flexShrink: 0, minWidth: 58,
                  }}>
                    {st.label}
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, lineHeight: 1.4 }}>
                    {r.detail}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ fontFamily: font, fontSize: "11.5px", fontWeight: 700, color: accent, flexShrink: 0, alignSelf: "center" }}>
            {row.reasons[0]?.verb || "Open"} →
          </Typography>
        </Box>
      ))}

      {!queue.length && (
        // Said in words, not as an empty grey box. A quiet queue is a real
        // answer and should read like one.
        <Box sx={{ px: 1.75, py: 2.5 }}>
          <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: success }}>
            Nothing needs you right now.
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.4, lineHeight: 1.5 }}>
            Rows appear here when a booking request is about to expire, a card is declined, a summary or drafted task
            is waiting on your approval, a task goes overdue, or a student falls behind the one-lesson-a-week pace.
          </Typography>
        </Box>
      )}

      {groups.length > 0 && (
        <Box sx={{ px: 1.75, py: 1.1, borderTop: `1px solid ${border}`, backgroundColor: "#FAF8F5" }}>
          <Typography sx={{
            fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", color: textSecondary, mb: 0.5,
          }}>
            Group sessions with seats left
          </Typography>
          {groups.map((g) => (
            <Box key={g.id} sx={{ display: "flex", alignItems: "baseline", gap: 1, py: 0.3 }}>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 600, color: textPrimary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {g.title}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, flexShrink: 0 }}>
                {moment(g.at).format("ddd D MMM")} · {g.going}/{g.capacity} · {g.unfilled} free
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
